import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getLead,
  updateLeadContactFlags,
  updateLeadFlags,
} from '@/services/outbound-calls-service/leads.service';
import { Lead, NumberFlagsPayload } from '@/services/outbound-calls-service/leads.service.types';
import { ReactQueryKeys } from '@/utils/react-query-keys';

export const useLead = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ReactQueryKeys.OUTBOUND_LEAD(id),
    queryFn: () => getLead(id),
    enabled: !!id,
  });

  return { lead: data, isLoading, error };
};

/** A flag skips numbers, so the entry tables show it too. */
const useInvalidateEntries = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ReactQueryKeys.OUTBOUND_CAMPAIGN_ENTRIES_ALL() });
};

export const useUpdateLeadFlags = (leadId: number) => {
  const queryClient = useQueryClient();
  const invalidateEntries = useInvalidateEntries();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (flags: NumberFlagsPayload) => updateLeadFlags(leadId, flags),
    onSuccess: (lead: Lead) => {
      queryClient.setQueryData(ReactQueryKeys.OUTBOUND_LEAD(leadId), lead);
      invalidateEntries();
    },
  });

  return { setLeadFlags: mutateAsync, isPending };
};

export const useUpdateLeadContactFlags = (leadId: number) => {
  const queryClient = useQueryClient();
  const invalidateEntries = useInvalidateEntries();

  const { mutateAsync, isPending, variables } = useMutation({
    mutationFn: ({ contactId, flags }: { contactId: number; flags: NumberFlagsPayload }) =>
      updateLeadContactFlags(contactId, flags),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.OUTBOUND_LEAD(leadId) });
      invalidateEntries();
    },
  });

  return {
    setContactFlags: mutateAsync,
    pendingContactId: isPending ? variables?.contactId : undefined,
  };
};
